import React, { useState, useContext } from 'react';
import {
  Card, Button, Select, Switch, Space, Typography, Row, Col,
  Divider, Tag, Alert
} from 'antd';
import {
  PlayCircleOutlined, TeamOutlined, UserOutlined, TrophyOutlined
} from '@ant-design/icons';
import { STUFE_CHOICES } from '../../api/services/equalOffenseService';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

/**
 * EqualOffenseStarter Component
 * UI for starting a new Equal Offense session
 */
const EqualOffenseStarter = ({
  onStart,
  players = [],
  currentPlayerId,
  loading = false,
}) => {
  const [stufe, setStufe] = useState(1);
  const [isMatchplay, setIsMatchplay] = useState(false);
  const [opponent, setOpponent] = useState(null);

  const selectedStufe = STUFE_CHOICES.find(s => s.value === stufe);

  // Filter out current player from opponent list
  const availableOpponents = players.filter(p => p.id !== currentPlayerId);

  const handleStart = () => {
    const sessionData = {
      stufe,
      is_matchplay: isMatchplay,
      opponent: isMatchplay ? opponent : null,
    };
    onStart(sessionData);
  };

  const canStart = !isMatchplay || (isMatchplay && opponent);

  return (
    <Card>
      <Title level={4}>
        <TrophyOutlined /> Equal Offense Training
      </Title>

      <Paragraph type="secondary">
        Equal Offense ist ein Trainings-Spielmodus basierend auf 14.1 Endlos.
        Wähle deine Stufe und starte eine Session.
      </Paragraph>

      <Divider />

      {/* Stufe Selection */}
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Schwierigkeitsstufe wählen:
        </Text>
        <Select
          value={stufe}
          onChange={setStufe}
          style={{ width: '100%' }}
          size="large"
        >
          {STUFE_CHOICES.map(s => (
            <Option key={s.value} value={s.value}>
              <Space>
                <Tag color={s.value <= 3 ? 'green' : s.value <= 6 ? 'orange' : 'red'}>
                  {s.value}
                </Tag>
                <span>{s.label}</span>
              </Space>
            </Option>
          ))}
        </Select>

        {/* Stufe Details */}
        {selectedStufe && (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message={selectedStufe.label}
            description={
              <div>
                <p><strong>Regeln:</strong> {selectedStufe.description}</p>
                {selectedStufe.value <= 3 && (
                  <p>Ball-in-Hand nach jedem Foul erlaubt die Spielball-Positionierung.</p>
                )}
                {selectedStufe.value === 7 && (
                  <p>God-Mode: Mindestens 250 Punkte pro Session. Bei Ball 14 wird neu aufgebaut (wie bei 14.1 Endlos).</p>
                )}
              </div>
            }
          />
        )}
      </div>

      {/* Matchplay Toggle */}
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Switch
            checked={isMatchplay}
            onChange={setIsMatchplay}
          />
          <Text strong>
            {isMatchplay ? (
              <><TeamOutlined /> Matchplay (gegen Gegner)</>
            ) : (
              <><UserOutlined /> Solo Training</>
            )}
          </Text>
        </Space>
      </div>

      {/* Opponent Selection (if matchplay) */}
      {isMatchplay && (
        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Gegner auswählen:
          </Text>
          <Select
            value={opponent}
            onChange={setOpponent}
            style={{ width: '100%' }}
            placeholder="Gegner wählen..."
            showSearch
            optionFilterProp="children"
          >
            {availableOpponents.map(p => (
              <Option key={p.id} value={p.id}>{p.name}</Option>
            ))}
          </Select>
        </div>
      )}

      <Divider />

      {/* Start Button */}
      <Button
        type="primary"
        size="large"
        icon={<PlayCircleOutlined />}
        onClick={handleStart}
        disabled={!canStart}
        loading={loading}
        block
      >
        {isMatchplay ? 'Matchplay starten' : 'Training starten'}
      </Button>

      {/* Quick Info */}
      <div style={{ marginTop: 24 }}>
        <Title level={5}>Wie funktioniert Equal Offense?</Title>
        <Row gutter={[16, 8]}>
          <Col span={24}>
            <Text type="secondary">
              • Baue 15 Bälle im Dreieck auf<br />
              • Versenke so viele Bälle wie möglich in einer Aufnahme<br />
              • Jeder versenkte Ball = 1 Punkt<br />
              • Bei Foul: Leben verlieren (je nach Stufe)<br />
              • Ziel: Möglichst hohe Punktzahl pro Session
            </Text>
          </Col>
        </Row>
      </div>
    </Card>
  );
};

export default EqualOffenseStarter;
